<template>
  <div class="basic-setup-tab">
    <div class="tab-header mb-6">
      <h3 class="text-lg font-semibold text-gray-900">Basic Configuration</h3>
      <p class="text-sm text-gray-600 mt-1">
        Set up the foundation and basic settings for your Salesforce integration
      </p>
    </div>

    <div class="form-sections space-y-6">
      <!-- Foundation Campaign Selection -->
      <div class="form-section">
        <div class="section-header mb-4">
          <h4 class="text-md font-medium text-gray-800">Foundation Campaign</h4>
          <p class="text-sm text-gray-600">
            Select which foundation campaign this configuration applies to
          </p>
        </div>

        <div class="form-group">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Foundation Campaign <span class="text-red-500">*</span>
          </label>
          <select 
            v-model="localForm.foundation_campaign_id"
            class="form-select w-full"
            :disabled="isEditMode"
            required
            @change="emitUpdate"
          >
            <option value="">Select a foundation campaign...</option>
            <option 
              v-for="foundation in availableFoundations" 
              :key="foundation.id"
              :value="String(foundation.id)"
            >
              {{ foundation.name }}
            </option>
          </select>
          
          <div v-if="isEditMode" class="mt-2">
            <p class="text-xs text-gray-500">
              <i class="fas fa-info-circle mr-1"></i>
              Foundation campaign cannot be changed after creation
            </p>
          </div>
          
          <div v-if="errors.foundation_campaign_id" class="mt-1">
            <p class="text-red-500 text-sm">{{ errors.foundation_campaign_id }}</p>
          </div>
        </div>
      </div>

      <!-- Enable/Disable Toggle -->
      <div class="form-section">
        <div class="section-header mb-4">
          <h4 class="text-md font-medium text-gray-800">Synchronization Status</h4>
          <p class="text-sm text-gray-600">
            Control whether this configuration actively syncs campaigns to Salesforce
          </p>
        </div>

        <div class="form-group">
          <label class="flex items-start space-x-3">
            <input 
              type="checkbox"
              v-model="localForm.enabled"
              class="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              @change="emitUpdate"
            >
            <div>
              <span class="text-sm font-medium text-gray-700">Enable synchronization</span>
              <p class="text-xs text-gray-500 mt-1">
                When enabled, new campaigns under this foundation will automatically sync to Salesforce.
                You can always disable this later if needed.
              </p>
            </div>
          </label>
        </div>
      </div>

      <!-- Template Selection (for new configurations) -->
      <div v-if="!isEditMode && templates.length > 0" class="form-section">
        <div class="section-header mb-4">
          <h4 class="text-md font-medium text-gray-800">Configuration Template</h4>
          <p class="text-sm text-gray-600">
            Choose a pre-built template to automatically configure field mappings and settings
          </p>
        </div>

        <div class="form-group">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Use Template (Optional)
          </label>
          <select 
            v-model="localForm.template_id"
            class="form-select w-full"
            @change="onTemplateChange"
          >
            <option value="">No template - I'll configure manually</option>
            <option 
              v-for="template in templates" 
              :key="template.id"
              :value="template.id"
            >
              {{ template.name }}
            </option>
          </select>
          
          <div v-if="selectedTemplate" class="mt-3 p-3 bg-blue-50 rounded-md">
            <div class="flex items-start space-x-2">
              <i class="fas fa-info-circle text-blue-500 mt-0.5"></i>
              <div>
                <p class="text-sm font-medium text-blue-800">{{ selectedTemplate.name }}</p>
                <p class="text-xs text-blue-600 mt-1">{{ selectedTemplate.description }}</p>
                <p class="text-xs text-blue-600 mt-2">
                  This template will pre-configure field mappings and connection settings. 
                  You can customize these in the next steps.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Setup Guide -->
      <div class="form-section">
        <div class="bg-gray-50 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-800 mb-3">
            <i class="fas fa-lightbulb text-yellow-500 mr-2"></i>
            Quick Setup Guide
          </h4>
          <div class="space-y-2 text-sm text-gray-600">
            <div class="flex items-start space-x-2">
              <span class="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">1</span>
              <span>Select your foundation campaign above</span>
            </div>
            <div class="flex items-start space-x-2">
              <span class="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">2</span>
              <span>Enable synchronization when you're ready to start syncing</span>
            </div>
            <div class="flex items-start space-x-2">
              <span class="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">3</span>
              <span>Choose a template to speed up configuration (recommended)</span>
            </div>
            <div class="flex items-start space-x-2">
              <span class="flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">4</span>
              <span>Continue to the next tabs to customize settings and field mappings</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';

export default {
  name: 'BasicSetupTab',
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    foundations: {
      type: Array,
      default: () => [],
    },
    templates: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'template-selected'],
  setup(props, { emit }) {
    const localForm = ref({
      foundation_campaign_id: '',
      enabled: false,
      template_id: '',
    });
    
    const errors = ref({});

    // Computed properties
    const isEditMode = computed(() => {
      return !!props.modelValue.foundation_campaign_id && props.modelValue.foundation_campaign_id !== '';
    });

    const availableFoundations = computed(() => {
      // In edit mode, show all foundations (dropdown is disabled anyway)
      // In create mode, we could filter out foundations that already have configurations
      return Array.isArray(props.foundations) ? props.foundations : [];
    });

    const selectedTemplate = computed(() => {
      if (!localForm.value.template_id) return null;
      return props.templates.find(template => template.id === localForm.value.template_id);
    });

    // Methods
    const validateForm = () => {
      errors.value = {};
      
      if (!localForm.value.foundation_campaign_id) {
        errors.value.foundation_campaign_id = 'Foundation campaign is required';
        return false;
      }
      
      return true;
    };

    const emitUpdate = () => {
      validateForm();
      emit('update:modelValue', { ...localForm.value });
    };

    const onTemplateChange = () => {
      if (selectedTemplate.value) {
        emit('template-selected', selectedTemplate.value);
      }
      emitUpdate();
    };

    // Initialize with prop values
    onMounted(() => {
      localForm.value = {
        foundation_campaign_id: props.modelValue.foundation_campaign_id || '',
        enabled: props.modelValue.enabled || false,
        template_id: props.modelValue.template_id || '',
      };
      validateForm();
    });

    // Watch for external prop changes - only update if values actually differ
    watch(() => props.modelValue, (newValue) => {
      if (newValue.foundation_campaign_id !== localForm.value.foundation_campaign_id ||
          newValue.enabled !== localForm.value.enabled ||
          newValue.template_id !== localForm.value.template_id) {
        localForm.value = {
          foundation_campaign_id: newValue.foundation_campaign_id || '',
          enabled: newValue.enabled || false,
          template_id: newValue.template_id || '',
        };
      }
    }, { deep: true });

    // Watch form changes and emit - removed to prevent circular updates
    // Updates are now explicitly triggered via @change events

    return {
      localForm,
      errors,
      isEditMode,
      availableFoundations,
      selectedTemplate,
      validateForm,
      emitUpdate,
      onTemplateChange,
    };
  },
};
</script>

<style scoped>
.form-select {
  @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500;
}

.form-section {
  @apply border-b border-gray-100 pb-6 last:border-b-0 last:pb-0;
}

.section-header h4 {
  @apply flex items-center space-x-2;
}

.tab-header {
  @apply border-b border-gray-200 pb-4;
}

@media (max-width: 640px) {
  .form-sections {
    @apply space-y-4;
  }
  
  .form-section {
    @apply pb-4;
  }
}
</style>